import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import liff from "@line/liff";

// Singleton guard — shared กับ useLiffAuth (ต้อง import จากที่เดียวกัน)
// ถ้า useLiffAuth อยู่คนละไฟล์ ให้ย้าย initLiffOnce ไปไว้ใน liffInstance.js แล้ว import ทั้งคู่
let liffInitPromise = null;

export const initLiffOnce = () => {
  if (!liffInitPromise) {
    liffInitPromise = liff.init({ liffId: process.env.REACT_APP_LIFF_ID });
  }
  return liffInitPromise;
};

const LiffProfileContext = createContext();

export const useLiffProfile = () => useContext(LiffProfileContext);

export const LiffProfileProvider = ({ children }) => {
  const [profile, setProfile] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [userId, setUserId] = useState(localStorage.getItem("userId"));
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const initializeProfile = async () => {
      try {
        if (!process.env.REACT_APP_LIFF_ID) {
          console.error("LIFF ID is not defined.");
          return;
        }

        await initLiffOnce(); // ใช้ singleton — ไม่ init ซ้ำถ้า useLiffAuth init ไปแล้ว

        if (!liff.isLoggedIn()) {
          // Context ไม่ทำ login เอง — ให้ useLiffAuth จัดการ
          return;
        }

        // ถ้า token ใน localStorage ยังใช้ได้ ไม่ต้อง getProfile ใหม่
        const storedToken = localStorage.getItem("authToken");
        const storedUserId = localStorage.getItem("userId");
        const freshToken = liff.getAccessToken();

        if (storedToken && storedUserId && storedToken === freshToken) {
          setAuthToken(storedToken);
          setUserId(storedUserId);

          // ถ้ายังไม่มี pictureUrl ใน localStorage → fetch profile เพิ่ม
          if (!localStorage.getItem("line_picture_url")) {
            try {
              const p = await liff.getProfile();
              if (p.pictureUrl) localStorage.setItem("line_picture_url", p.pictureUrl);
              if (p.displayName) localStorage.setItem("line_display_name", p.displayName);
              setProfile(p);
            } catch (e) {
              console.warn("LIFF getProfile for picture failed:", e);
            }
          }
          return;
        }

        // Token เปลี่ยน หรือยังไม่มีใน storage → fetch profile ใหม่
        const userProfile = await liff.getProfile();

        setProfile(userProfile);
        setUserId(userProfile.userId);
        setAuthToken(freshToken);

        localStorage.setItem("authToken", freshToken);
        localStorage.setItem("userId", userProfile.userId);
        if (userProfile.pictureUrl) localStorage.setItem("line_picture_url", userProfile.pictureUrl);
        if (userProfile.displayName) localStorage.setItem("line_display_name", userProfile.displayName);
      } catch (error) {
        console.error("LIFF profile initialization failed:", error);
      }
    };

    initializeProfile();
  }, []);

  // ให้ context sync เมื่อ localStorage เปลี่ยน (เช่น หลัง logout จาก tab อื่น)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "authToken") setAuthToken(e.newValue);
      if (e.key === "userId") setUserId(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleLogin = () => {
    if (!liff.isLoggedIn()) {
      liff.login({ redirectUri: window.location.href });
    }
  };

  // expose setProfile ให้ useLiffAuth สามารถ sync profile จากนอก context ได้
  return (
    <LiffProfileContext.Provider
      value={{ profile, authToken, userId, handleLogin, setProfile: (p) => {
        setProfile(p.profile ?? null);
        setAuthToken(p.authToken ?? null);
        setUserId(p.userId ?? null);
        if (p.authToken) localStorage.setItem("authToken", p.authToken);
        else localStorage.removeItem("authToken");
        if (p.userId) localStorage.setItem("userId", p.userId);
        else localStorage.removeItem("userId");
        if (p.profile?.displayName) localStorage.setItem("line_display_name", p.profile.displayName);
        if (p.profile?.pictureUrl) localStorage.setItem("line_picture_url", p.profile.pictureUrl);
      }}}
    >
      {children}
    </LiffProfileContext.Provider>
  );
};
